import React, { useEffect, useState } from 'react';

interface CountdownCircleProps {
  duration?: number; // Duration in seconds, default to 10
  onComplete?: () => void; // Callback when countdown completes
  onClose?: () => void; // Callback when close button is clicked
  size?: number; // Size of the circle in pixels, default to 100
  strokeWidth?: number; // Width of the stroke, default to 8
  className?: string; // Additional CSS classes
}

export const CountdownCircle: React.FC<CountdownCircleProps> = ({
  duration = 10,
  onComplete,
  onClose,
  size = 100,
  strokeWidth = 8,
  className = ''
}) => {
  const [timeLeft, setTimeLeft] = useState(duration);
  const [isActive, setIsActive] = useState(false);

  // Add padding to prevent clipping of stroke and effects
  const padding = strokeWidth + 4; // Extra padding for effects like glow
  const svgSize = size + padding * 2;
  const radius = size / 2 - strokeWidth / 2;
  const center = svgSize / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - ((duration - timeLeft) / duration) * circumference;

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft => {
          if (timeLeft <= 1) {
            setIsActive(false);
          }
          return timeLeft - 1;
        });
      }, 1000);
    } else if (timeLeft === 0) {
      setIsActive(false);
      onComplete?.();
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isActive, timeLeft, onComplete]);



  const reset = () => {
    setTimeLeft(duration);
    setIsActive(false);
  };

  const pause = () => {
    setIsActive(false);
  };

  const resume = () => {
    if (timeLeft > 0) {
      setIsActive(true);
    }
  };

  return (
    <div
      className="countdown-circle">
      <div className="countdown-circle-svg-container" style={{ position: 'relative', width: svgSize, height: svgSize }}>
        <svg
          width={svgSize}
          height={svgSize}
          className="countdown-circle-svg"
        >
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke="#374151"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          {/* Progress circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={timeLeft > 3 ? "#10B981" : timeLeft > 1 ? "#F59E0B" : "#EF4444"}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="countdown-circle-progress"
          />
        </svg>
        {/* Time display - inside the container for true centering */}
        <div
          className={`countdown-circle-time ${timeLeft > 3 ? 'time-safe' : timeLeft > 1 ? 'time-warning' : 'time-danger'}`}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: size * 0.4, // Increased size for better visibility
            lineHeight: 1,
            margin: 0,
          }}
        >
          {timeLeft > 0 ? timeLeft : '✓'}
        </div>
      </div>
      {/* Time display */}


      {/* Control buttons */}
      <div className="countdown-circle-controls">
        <button
          onClick={reset}
          className="countdown-circle-control-btn reset"
        >
          Reiniciar
        </button>
        <button
          onClick={isActive ? pause : resume}
          className={`countdown-circle-control-btn ${isActive ? 'pause' : 'play'}`}
        >
          {isActive ? 'Parar' : 'Iniciar'}
        </button>
      </div>
    </div>
  );
};

export default CountdownCircle;